<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import { LazyVChart } from '~/components/ui/chart'
import type { IBankingBlockResponseMap, IBankingSectorsFrame } from '../types'
import { useBankingNumber } from '../useBankingNumber'
import { wrapLabel } from './chart-labels'
import WidgetBankingStatsBlock from '../WidgetBankingStatsBlock.vue'

/** Sector keys in the order of the API; also fixes each sector's colour. */
const SECTORS = [
  'industry',
  'agriculture',
  'transport',
  'communication',
  'trade',
  'processing',
  'construction',
  'mortgage',
  'consumer',
  'social',
  'other'
] as const

type Sector = (typeof SECTORS)[number]

const SECTOR_COLORS: Record<Sector, string> = {
  industry: '#475569',
  agriculture: '#16a34a',
  transport: '#0891b2',
  communication: '#7c3aed',
  trade: '#2563eb',
  processing: '#a16207',
  construction: '#ea580c',
  mortgage: '#db2777',
  consumer: '#dc2626',
  social: '#0d9488',
  other: '#9ca3af'
}

/** One frame of the race lasts this long, the bars move between frames over the same time. */
const FRAME_MS = 900

const { t } = useI18n()
const { formatSom, formatPercent, formatMonth } = useBankingNumber()

const { data: response } = useApi<IBankingBlockResponseMap['sectors']>('/banking-statistics/blocks/sectors', {
  key: 'banking-statistics-sectors',
  lazy: true
})

const frames = computed<IBankingSectorsFrame[]>(() => response.value?.data.frames ?? [])
const rates = computed<Partial<Record<Sector, number | null>>>(() => {
  const value = response.value?.data.rates

  return value && !Array.isArray(value) ? value : {}
})
const asOf = computed(() => frames.value.at(-1)?.period ?? null)

const sectorName = (sector: string): string => t(`banking_statistics.sectors.names.${sector}`)

const isSector = (key: string): key is Sector => (SECTORS as readonly string[]).includes(key)

/** Sectors with a rate, the most expensive first. */
const sortedRates = computed(() =>
  Object.entries(rates.value)
    .flatMap(([sector, rate]) => (isSector(sector) && typeof rate === 'number' ? [{ sector, rate }] : []))
    .sort((a, b) => b.rate - a.rate)
)

const title = computed<{ key: string; params: Record<string, string | number> }>(() => {
  const top = sortedRates.value[0]

  if (!top) {
    return { key: 'banking_statistics.sectors.title_neutral', params: {} }
  }

  return {
    key: 'banking_statistics.sectors.title',
    params: {
      sector: t(`banking_statistics.sectors.names_title.${top.sector}`),
      rate: formatPercent(top.rate)
    }
  }
})

// null stands for the latest frame, so the server and the browser render the same frame.
const selectedIndex = ref<number | null>(null)
const frameIndex = computed(() => selectedIndex.value ?? Math.max(frames.value.length - 1, 0))
const currentFrame = computed(() => frames.value[frameIndex.value] ?? null)

/** Year ends are shown as the year, the latest (incomplete) year as its month. */
const frameLabel = (frame: IBankingSectorsFrame | null): string => {
  if (!frame) return ''

  return frame.period.slice(5, 7) === '12' ? frame.period.slice(0, 4) : formatMonth(frame.period)
}

const isPlaying = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

const stop = () => {
  if (timer !== null) clearInterval(timer)
  timer = null
  isPlaying.value = false
}

const play = () => {
  if (isPlaying.value) {
    stop()

    return
  }

  const last = frames.value.length - 1

  if (last < 1) return

  // From the start when the race already stands on the last frame, otherwise from where it is.
  if (frameIndex.value >= last) selectedIndex.value = 0

  isPlaying.value = true
  timer = setInterval(() => {
    const next = frameIndex.value + 1

    selectedIndex.value = Math.min(next, last)

    if (next >= last) stop()
  }, FRAME_MS)
}

const onSlide = (value: number[] | undefined) => {
  stop()
  selectedIndex.value = value?.[0] ?? null
}

onBeforeUnmount(stop)

const raceOption = computed<EChartsOption>(() => {
  const values = currentFrame.value?.values ?? {}

  return {
    grid: { left: 8, right: 96, top: 8, bottom: 8, containLabel: true },
    xAxis: {
      type: 'value',
      max: 'dataMax',
      axisLabel: { show: false },
      splitLine: { lineStyle: { color: '#f3f4f6' } }
    },
    yAxis: {
      type: 'category',
      data: SECTORS.map(sectorName),
      inverse: true,
      max: SECTORS.length - 1,
      animationDuration: FRAME_MS / 3,
      animationDurationUpdate: FRAME_MS / 3,
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: { fontSize: 11, color: '#374151', formatter: (value: string) => wrapLabel(value) }
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const { name, value } = params as { name?: string; value?: unknown }

        return typeof value === 'number' ? `${name}: <strong>${formatSom(value)}</strong>` : (name ?? '')
      }
    },
    series: [
      {
        type: 'bar',
        realtimeSort: true,
        barCategoryGap: '25%',
        data: SECTORS.map((sector) => ({
          value: values[sector] ?? 0,
          itemStyle: { color: SECTOR_COLORS[sector], borderRadius: [0, 3, 3, 0] }
        })),
        label: {
          show: true,
          position: 'right',
          valueAnimation: true,
          fontSize: 11,
          color: '#111827',
          formatter: (params: { value?: unknown }) =>
            typeof params.value === 'number' && params.value > 0 ? formatSom(params.value) : ''
        }
      }
    ],
    animationDuration: 0,
    animationDurationUpdate: FRAME_MS,
    animationEasing: 'linear',
    animationEasingUpdate: 'linear'
  }
})

const ratesOption = computed<EChartsOption>(() => ({
  grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
  xAxis: { type: 'value', axisLabel: { show: false }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
  yAxis: {
    type: 'category',
    inverse: true,
    data: sortedRates.value.map((item) => sectorName(item.sector)),
    axisTick: { show: false },
    axisLine: { show: false },
    axisLabel: { fontSize: 11, color: '#374151', formatter: (value: string) => wrapLabel(value) }
  },
  tooltip: {
    trigger: 'item',
    formatter: (params: unknown) => {
      const { name, value } = params as { name?: string; value?: unknown }

      return typeof value === 'number' ? `${name}: <strong>${formatPercent(value)}</strong>` : (name ?? '')
    }
  },
  series: [
    {
      type: 'bar',
      barCategoryGap: '25%',
      data: sortedRates.value.map((item) => ({
        value: item.rate,
        itemStyle: { color: SECTOR_COLORS[item.sector], borderRadius: [0, 3, 3, 0] }
      })),
      label: {
        show: true,
        position: 'right',
        fontSize: 11,
        color: '#111827',
        formatter: (params: { value?: unknown }) =>
          typeof params.value === 'number' ? formatPercent(params.value) : ''
      }
    }
  ]
}))
</script>

<template>
  <WidgetBankingStatsBlock id="sectors" :title-key="title.key" :title-params="title.params" :as-of="asOf">
    <div class="grid gap-6 lg:grid-cols-5">
      <div class="min-w-0 lg:col-span-3">
        <h3 class="mb-2 text-sm font-medium text-gray-700">{{ t('banking_statistics.sectors.portfolio') }}</h3>

        <div class="mb-3 flex items-center gap-3">
          <Button
            variant="blue"
            size="sm"
            class="shrink-0"
            :aria-pressed="isPlaying"
            :disabled="frames.length < 2"
            @click="play"
          >
            {{ t(isPlaying ? 'banking_statistics.sectors.pause' : 'banking_statistics.sectors.play') }}
          </Button>
          <Slider
            class="min-w-0 flex-1"
            :model-value="[frameIndex]"
            :min="0"
            :max="Math.max(frames.length - 1, 0)"
            :step="1"
            :aria-label-name="t('banking_statistics.sectors.year')"
            @update:model-value="onSlide"
          />
          <span
            data-testid="sectors-year"
            class="w-16 shrink-0 text-right text-lg font-semibold tabular-nums text-gray-900"
            aria-live="polite"
          >
            {{ frameLabel(currentFrame) }}
          </span>
        </div>

        <LazyVChart :option="raceOption" autoresize class="h-96 w-full" />
      </div>

      <div v-if="sortedRates.length" class="min-w-0 lg:col-span-2">
        <h3 class="mb-2 text-sm font-medium text-gray-700">{{ t('banking_statistics.sectors.rates') }}</h3>
        <LazyVChart :option="ratesOption" autoresize class="h-96 w-full" />
      </div>
    </div>
  </WidgetBankingStatsBlock>
</template>
